import { GeneralConfig } from '../config/generalConfig';
import { useAuthStore } from '../stores/authStore';
import { useCurrencyStore } from '../stores/currencyStore';
import { useDateFormatStore } from '../stores/dateFormatStore';
import { useDemoInfoStore } from '../stores/demoInfoStore';
import { useDemoStoreStore } from '../stores/demoStoreStore';
import { useHomeDashboardExtraStore } from '../stores/home/homeDashboardExtraStore';
import { useHomeDashboardStore } from '../stores/home/homeDashboardStore';
import { useHomePackageStore } from '../stores/home/homePackageStore';
import { useHomeRankStore } from '../stores/home/homeRankStore';
import { useLanguageStore } from '../stores/languageStore';
import { useMailInboxStore } from '../stores/mail/mailInboxStore';
import { useMailSentStore } from '../stores/mail/mailSentStore';
import { useMasterDemoStore } from '../stores/masterDemoStore';
import { useModuleStore } from '../stores/moduleStore';
import { useNavigationStore } from '../stores/navigationStore';
import { useGenealogyStore } from '../stores/network/genealogyStore';
import { useStoreSettingStore } from '../stores/storeSettingStore';
import { useArticlesStore } from '../stores/story/articlesStore';
import { useVideosStore } from '../stores/story/videosStore';
import useTabStore from '../stores/tabStore';
import { useWalletEarningsStore } from '../stores/wallet/walletEarningsStore';
import { useWalletPayoutStore } from '../stores/wallet/walletPayoutStore';
import { useWalletStatementStore } from '../stores/wallet/walletStatementStore';
import { Currency } from '../types/Currency';
import { CompensationPlan } from '../types/enums/CompensationPlan';
import { Language } from '../types/Language';
import { User } from '../types/User';
import {
  useNavigateAfterLogin,
  useNavigateAfterLogout,
} from './navigationUtils';
import { setStoreAuthToken } from './storage';

export const useLoginSuccess = () => {
  const { navigateAfterLogin } = useNavigateAfterLogin();

  const loginSuccess = (
    response: Record<string, any>,
    isSsoLogin: boolean = false
  ) => {
    const { setMasterDemoStatus } = useMasterDemoStore.getState();
    const { setDemoStoreId } = useDemoStoreStore.getState();
    const { updateDefaultStore } = useStoreSettingStore.getState();
    const { data: modules, updateCompensationPlan } = useModuleStore.getState();
    const { authenticate, setSsoToken } = useAuthStore.getState();
    const { setCurrency } = useCurrencyStore.getState();
    const { setLanguage } = useLanguageStore.getState();
    const { setFormat: setDateFormat } = useDateFormatStore.getState();
    const { toggleDemo } = useDemoInfoStore.getState();

    let isDemoAllowed = true;
    const isUserActive = response.user_status as boolean;

    if (GeneralConfig.isDemo) {
      isDemoAllowed = response.user_allowed as boolean;
      toggleDemo(isDemoAllowed);
      const isMasterDemo = response.is_master_demo as boolean;
      setMasterDemoStatus(isMasterDemo);
      const plan = response.compensation_plan as CompensationPlan;
      updateCompensationPlan(plan);

      if (modules?.store) {
        const defaultStoreId = response.default_store_id as number;
        setDemoStoreId(defaultStoreId);
        updateDefaultStore(defaultStoreId);
      }
    }

    if (isDemoAllowed && isUserActive) {
      if (modules?.store && isSsoLogin) {
        setStoreAuthToken(response.store_token as string);
      }

      const authToken = response.token as string;
      const user = User.fromJson(response.user as Record<string, any>);
      const currency = Currency.fromJson(
        response.default_currency as Record<string, any>
      );
      const language = Language.fromJson(
        response.default_language as Record<string, any>
      );
      authenticate(authToken, user);
      if (modules?.store && !isSsoLogin) {
        setSsoToken(response.sso_token as string);
      }
      setCurrency(currency);
      setLanguage(language.code);
      setDateFormat(user.dateFormat);
    }

    navigateAfterLogin(isDemoAllowed, isUserActive);
  };

  return { loginSuccess };
};

export const useLogoutSuccess = () => {
  const { navigateAfterLogout } = useNavigateAfterLogout();
  const { resetTabs } = useTabStore();
  const { reset: resetHomeDashboard } = useHomeDashboardStore();
  const { reset: resetHomePackage } = useHomePackageStore();
  const { reset: resetHomeRank } = useHomeRankStore();
  const { reset: resetHomeDashboardExtra } = useHomeDashboardExtraStore();
  const { reset: resetWalletStatement } = useWalletStatementStore();
  const { reset: resetWalletEarnings } = useWalletEarningsStore();
  const { reset: resetWalletPayout } = useWalletPayoutStore();
  const { reset: resetNetworkGenealogy } = useGenealogyStore();
  const { refresh: resetArticles } = useArticlesStore();
  const { refresh: resetVideos } = useVideosStore();
  const { refresh: resetMailInbox } = useMailInboxStore();
  const { refresh: resetMailSent } = useMailSentStore();

  const logoutSuccess = (
    response: Record<string, any>,
    isSsoLogout: boolean = false
  ) => {
    const { unauthenticate, setSsoLogoutToken } = useAuthStore.getState();
    const { setCurrentMenu } = useNavigationStore.getState();
    const { data: modules } = useModuleStore.getState();

    unauthenticate();
    if (modules?.store && !isSsoLogout) {
      setSsoLogoutToken(response.sso_logout_token as string);
    }
    resetTabs();
    setCurrentMenu('home');
    resetHomeDashboard();
    resetHomePackage();
    resetHomeRank();
    resetHomeDashboardExtra();
    resetWalletStatement();
    resetWalletEarnings();
    resetWalletPayout();
    resetNetworkGenealogy();
    resetArticles();
    resetVideos();
    resetMailInbox();
    resetMailSent();

    // navigateAfterLogout();
  };

  return { logoutSuccess };
};
